import { SupportedLanguage } from "@/components/multilingual/LanguageSelector";

export interface CancellationPolicyTranslation {
  title: string;
  breadcrumb: string;
  lastUpdatedLabel: string;
  withdrawalRight: {
    title: string;
    paragraphs: string[];
  };
  howToExercise: {
    title: string;
    description: string;
  };
  controller: {
    name: string;
    address: string;
    email: string;
  };
  consequences: {
    title: string;
    paragraphs: string[];
  };
  digitalContentNotice: {
    title: string;
    content: string;
  };
  withdrawalForm: {
    title: string;
    instruction: string;
    toLabel: string;
    declarationText: string;
    orderedOnLabel: string;
    consumerNameLabel: string;
    consumerAddressLabel: string;
    signatureLabel: string;
    dateLabel: string;
  };
}

const controllerInfo = {
  name: "PaperVerse UG (haftungsbeschränkt)",
  address: "Moritzburger Str. 39\n01127 Dresden\nGermany",
  email: "contact@paperverse.io",
};

export const cancellationPolicyTranslations: Record<SupportedLanguage, CancellationPolicyTranslation> = {
  de: {
    title: "Widerrufsbelehrung",
    breadcrumb: "Widerrufsbelehrung",
    lastUpdatedLabel: "Zuletzt aktualisiert",
    withdrawalRight: {
      title: "Widerrufsrecht",
      paragraphs: [
        "Sie haben das Recht, binnen vierzehn Tagen ohne Angabe von Gründen diesen Vertrag zu widerrufen.",
        "Die Widerrufsfrist beträgt vierzehn Tage ab dem Tag des Vertragsabschlusses.",
        "Bei Abonnements (monatliche Credit-Pläne) beginnt die Widerrufsfrist mit Abschluss des Abonnementvertrags. Die automatische Verlängerung des Abonnements ist kein neuer Vertragsschluss und löst kein erneutes Widerrufsrecht aus; Sie können das Abonnement jedoch jederzeit zum Ende der laufenden Abrechnungsperiode ordentlich kündigen – u. a. über die Kündigungsschaltfläche unter /cancel-contract (§ 312k BGB).",
      ],
    },
    howToExercise: {
      title: "Ausübung des Widerrufsrechts",
      description:
        "Um Ihr Widerrufsrecht auszuüben, müssen Sie uns mittels einer eindeutigen Erklärung (z.B. ein mit der Post versandter Brief oder E-Mail) über Ihren Entschluss, diesen Vertrag zu widerrufen, informieren. Sie können dafür das beigefügte Muster-Widerrufsformular verwenden, das jedoch nicht vorgeschrieben ist.",
    },
    controller: controllerInfo,
    consequences: {
      title: "Folgen des Widerrufs",
      paragraphs: [
        "Wenn Sie diesen Vertrag widerrufen, haben wir Ihnen alle Zahlungen, die wir von Ihnen erhalten haben, einschließlich der Lieferkosten (mit Ausnahme der zusätzlichen Kosten, die sich daraus ergeben, dass Sie eine andere Art der Lieferung als die von uns angebotene, günstigste Standardlieferung gewählt haben), unverzüglich und spätestens binnen vierzehn Tagen ab dem Tag zurückzuzahlen, an dem die Mitteilung über Ihren Widerruf dieses Vertrags bei uns eingegangen ist.",
        "Für diese Rückzahlung verwenden wir dasselbe Zahlungsmittel, das Sie bei der ursprünglichen Transaktion eingesetzt haben, es sei denn, mit Ihnen wurde ausdrücklich etwas anderes vereinbart; in keinem Fall werden Ihnen wegen dieser Rückzahlung Entgelte berechnet.",
      ],
    },
    digitalContentNotice: {
      title: "Besonderer Hinweis für digitale Inhalte",
      content:
        "Das Widerrufsrecht erlischt bei einem Vertrag über die Lieferung von nicht auf einem körperlichen Datenträger befindlichen digitalen Inhalten, wenn der Unternehmer mit der Ausführung des Vertrags begonnen hat, nachdem der Verbraucher ausdrücklich zugestimmt hat, dass der Unternehmer mit der Ausführung des Vertrags vor Ablauf der Widerrufsfrist beginnt, und seine Kenntnis davon bestätigt hat, dass er durch seine Zustimmung mit Beginn der Ausführung des Vertrags sein Widerrufsrecht verliert (§ 356 Abs. 5 BGB).",
    },
    withdrawalForm: {
      title: "Muster-Widerrufsformular",
      instruction:
        "(Wenn Sie den Vertrag widerrufen wollen, dann füllen Sie bitte dieses Formular aus und senden Sie es zurück.)",
      toLabel: "An",
      declarationText:
        "Hiermit widerrufe(n) ich/wir (*) den von mir/uns (*) abgeschlossenen Vertrag über den Kauf der folgenden Waren (*)/die Erbringung der folgenden Dienstleistung (*): PaperVerse-Abonnement (monatlicher Credit-Plan) bzw. einmaliges Credit-Paket (Zutreffendes bitte angeben)",
      orderedOnLabel: "Bestellt am/erhalten am (*)",
      consumerNameLabel: "Name des/der Verbraucher(s)",
      consumerAddressLabel: "Anschrift des/der Verbraucher(s)",
      signatureLabel: "Unterschrift des/der Verbraucher(s) (nur bei Mitteilung auf Papier)",
      dateLabel: "Datum",
    },
  },
  en: {
    title: "Cancellation Policy",
    breadcrumb: "Cancellation Policy",
    lastUpdatedLabel: "Last updated",
    withdrawalRight: {
      title: "Right of Withdrawal",
      paragraphs: [
        "You have the right to withdraw from this contract within fourteen days without giving any reason.",
        "The withdrawal period will expire after fourteen days from the day of the conclusion of the contract.",
        "For subscriptions (monthly credit plans), the withdrawal period starts when the subscription contract is concluded. Automatic renewal of the subscription is not a new contract and does not trigger a new right of withdrawal; you may, however, cancel the subscription at any time with effect from the end of the current billing period, including via the cancellation button at /cancel-contract (Section 312k BGB).",
      ],
    },
    howToExercise: {
      title: "How to Exercise the Right of Withdrawal",
      description:
        "To exercise the right of withdrawal, you must inform us of your decision to withdraw from this contract by an unequivocal statement (e.g., a letter sent by post or email). You may use the attached model withdrawal form, but it is not obligatory.",
    },
    controller: controllerInfo,
    consequences: {
      title: "Consequences of Withdrawal",
      paragraphs: [
        "If you withdraw from this contract, we shall reimburse to you all payments received from you, including the costs of delivery (with the exception of the supplementary costs resulting from your choice of a type of delivery other than the least expensive type of standard delivery offered by us), without undue delay and in any event not later than fourteen days from the day on which we are informed about your decision to withdraw from this contract.",
        "We will carry out such reimbursement using the same means of payment as you used for the initial transaction, unless you have expressly agreed otherwise; in any event, you will not incur any fees as a result of such reimbursement.",
      ],
    },
    digitalContentNotice: {
      title: "Special Notice for Digital Content",
      content:
        "The right of withdrawal expires for a contract for the supply of digital content which is not supplied on a tangible medium if the performance has begun with the consumer's prior express consent and their acknowledgement that they thereby lose their right of withdrawal (Section 356(5) BGB).",
    },
    withdrawalForm: {
      title: "Model Withdrawal Form",
      instruction:
        "(If you wish to withdraw from the contract, please fill out this form and send it back.)",
      toLabel: "To",
      declarationText:
        "I/We (*) hereby give notice that I/We (*) withdraw from my/our (*) contract of sale of the following goods (*)/for the provision of the following service (*): PaperVerse subscription (monthly credit plan) or one-off credit top-up pack (please specify)",
      orderedOnLabel: "Ordered on/received on (*)",
      consumerNameLabel: "Name of consumer(s)",
      consumerAddressLabel: "Address of consumer(s)",
      signatureLabel: "Signature of consumer(s) (only if this form is notified on paper)",
      dateLabel: "Date",
    },
  },
  fr: {
    title: "Politique d'Annulation",
    breadcrumb: "Politique d'Annulation",
    lastUpdatedLabel: "Dernière mise à jour",
    withdrawalRight: {
      title: "Droit de Rétractation",
      paragraphs: [
        "Vous avez le droit de vous rétracter du présent contrat dans un délai de quatorze jours sans donner de motif.",
        "Le délai de rétractation expire quatorze jours après le jour de la conclusion du contrat.",
        "Pour les abonnements (forfaits de crédits mensuels), le délai de rétractation court à compter de la conclusion du contrat d'abonnement. Le renouvellement automatique ne constitue pas un nouveau contrat et n'ouvre pas un nouveau droit de rétractation ; vous pouvez toutefois résilier l'abonnement à tout moment avec effet à la fin de la période de facturation en cours, notamment via le bouton de résiliation à l'adresse /cancel-contract (§ 312k BGB).",
      ],
    },
    howToExercise: {
      title: "Comment Exercer le Droit de Rétractation",
      description:
        "Pour exercer le droit de rétractation, vous devez nous notifier votre décision de rétractation du présent contrat au moyen d'une déclaration dénuée d'ambiguïté (par exemple, lettre envoyée par la poste ou courrier électronique). Vous pouvez utiliser le modèle de formulaire de rétractation ci-joint, mais ce n'est pas obligatoire.",
    },
    controller: controllerInfo,
    consequences: {
      title: "Effets de la Rétractation",
      paragraphs: [
        "Si vous vous rétractez du présent contrat, nous vous rembourserons tous les paiements reçus de vous, y compris les frais de livraison (à l'exception des frais supplémentaires découlant du fait que vous avez choisi, le cas échéant, un mode de livraison autre que le mode moins coûteux de livraison standard proposé par nous) sans retard excessif et, en tout état de cause, au plus tard quatorze jours à compter du jour où nous sommes informés de votre décision de rétractation du présent contrat.",
        "Nous procéderons au remboursement en utilisant le même moyen de paiement que celui que vous aurez utilisé pour la transaction initiale, sauf si vous convenez expressément d'un moyen différent ; en tout état de cause, ce remboursement n'occasionnera pas de frais pour vous.",
      ],
    },
    digitalContentNotice: {
      title: "Avis Spécial pour le Contenu Numérique",
      content:
        "Le droit de rétractation expire pour un contrat de fourniture de contenu numérique non fourni sur un support matériel si l'exécution a commencé avec l'accord préalable exprès du consommateur et sa reconnaissance qu'il perd ainsi son droit de rétractation (§ 356 alinéa 5 BGB).",
    },
    withdrawalForm: {
      title: "Modèle de Formulaire de Rétractation",
      instruction:
        "(Si vous souhaitez vous rétracter du contrat, veuillez remplir ce formulaire et nous le renvoyer.)",
      toLabel: "À",
      declarationText:
        "Je/Nous (*) notifie/notifions (*) par la présente ma/notre (*) rétractation du contrat portant sur la vente des biens (*)/la prestation de services (*) ci-dessous : abonnement PaperVerse (forfait de crédits mensuel) ou pack de crédits ponctuel (veuillez préciser)",
      orderedOnLabel: "Commandé le/reçu le (*)",
      consumerNameLabel: "Nom du/des consommateur(s)",
      consumerAddressLabel: "Adresse du/des consommateur(s)",
      signatureLabel: "Signature du/des consommateur(s) (uniquement en cas de notification sur papier)",
      dateLabel: "Date",
    },
  },
  es: {
    title: "Política de Cancelación",
    breadcrumb: "Política de Cancelación",
    lastUpdatedLabel: "Última actualización",
    withdrawalRight: {
      title: "Derecho de Desistimiento",
      paragraphs: [
        "Tiene usted derecho a desistir del presente contrato en un plazo de catorce días sin necesidad de justificación.",
        "El plazo de desistimiento expirará a los catorce días del día de la celebración del contrato.",
        "En el caso de suscripciones (planes mensuales de créditos), el plazo de desistimiento comienza con la celebración del contrato de suscripción. La renovación automática no constituye un nuevo contrato ni genera un nuevo derecho de desistimiento; no obstante, puede cancelar la suscripción en cualquier momento con efectos al final del período de facturación en curso, también mediante el botón de cancelación en /cancel-contract (§ 312k BGB).",
      ],
    },
    howToExercise: {
      title: "Cómo Ejercer el Derecho de Desistimiento",
      description:
        "Para ejercer el derecho de desistimiento, deberá usted notificarnos su decisión de desistir del contrato a través de una declaración inequívoca (por ejemplo, una carta enviada por correo postal o correo electrónico). Podrá utilizar el modelo de formulario de desistimiento adjunto, aunque su uso no es obligatorio.",
    },
    controller: controllerInfo,
    consequences: {
      title: "Consecuencias del Desistimiento",
      paragraphs: [
        "Si usted desiste del presente contrato, le devolveremos todos los pagos recibidos de usted, incluidos los gastos de entrega (con la excepción de los gastos adicionales resultantes de la elección por su parte de una modalidad de entrega diferente a la modalidad menos costosa de entrega ordinaria que ofrezcamos) sin ninguna demora indebida y, en todo caso, a más tardar catorce días a partir de la fecha en que se nos informe de su decisión de desistir del presente contrato.",
        "Procederemos a efectuar dicho reembolso utilizando el mismo medio de pago empleado por usted para la transacción inicial, salvo que haya usted dispuesto expresamente lo contrario; en todo caso, no incurrirá en ningún gasto como consecuencia del reembolso.",
      ],
    },
    digitalContentNotice: {
      title: "Aviso Especial para Contenido Digital",
      content:
        "El derecho de desistimiento expira en el caso de un contrato para el suministro de contenido digital que no se suministre en un soporte material si la ejecución ha comenzado con el consentimiento expreso previo del consumidor y su reconocimiento de que con ello pierde su derecho de desistimiento (§ 356 apartado 5 BGB).",
    },
    withdrawalForm: {
      title: "Modelo de Formulario de Desistimiento",
      instruction:
        "(Si desea desistir del contrato, cumplimente este formulario y remítanoslo.)",
      toLabel: "A",
      declarationText:
        "Por la presente comunico/comunicamos (*) que desisto de mi/desistimos de nuestro (*) contrato de venta de los siguientes bienes (*)/prestación del siguiente servicio (*): suscripción de PaperVerse (plan mensual de créditos) o paquete puntual de créditos (indíquese lo que proceda)",
      orderedOnLabel: "Pedido con fecha/recibido con fecha (*)",
      consumerNameLabel: "Nombre del/de los consumidor(es)",
      consumerAddressLabel: "Dirección del/de los consumidor(es)",
      signatureLabel: "Firma del/de los consumidor(es) (solo si este formulario se notifica en papel)",
      dateLabel: "Fecha",
    },
  },
};
